import type { DateRange } from "react-day-picker";

import { type ClassValue, clsx } from "clsx";
import { differenceInDays } from "date-fns";
import { toast } from "sonner";
import { twMerge } from "tailwind-merge";

import type { EntityModelFieldResponse } from "@/actions/entityModelFields";
import type {
  MappingOption,
  MappingOptionItem,
} from "@/components/lists/[id]/types";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function isEmailValid(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function formatTimestampToUserTZ(timestamp: number | string): string {
  const date = new Date(timestamp);

  try {
    return new Intl.DateTimeFormat("en-US", {
      month: "numeric",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  } catch {
    return "Invalid Date";
  }
}

export function formatNumber(
  value: number,
  options: Intl.NumberFormatOptions = {
    maximumFractionDigits: 0,
  },
): string {
  const formatter = new Intl.NumberFormat("en-US", options);
  return formatter.format(value);
}

export function getDayPeriod(date: DateRange | undefined) {
  if (date?.from && date?.to) {
    const dayDifference = differenceInDays(date.to, date.from) + 1; // Add 1 to include both start and end dates
    return `${dayDifference} day period`;
  }
  return "";
}

// Following functions create code_verifier and code_challenge values for PKCE for Klaviyo OAuth - https://condatis.com/news/blog/oauth-confidential-clients/
function base64URLEncode(str: string): string {
  return Buffer.from(str)
    .toString("base64")
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
}

async function sha256(buffer: BufferSource): Promise<Uint8Array> {
  const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
  return new Uint8Array(hashBuffer);
}

export async function generateCodes(): Promise<{
  codeVerifier: string;
  codeChallenge: string;
}> {
  const verifierArray = crypto.getRandomValues(new Uint8Array(32));
  const verifier = base64URLEncode(
    String.fromCharCode(...Array.from(verifierArray)),
  );
  const hashArray = await sha256(new TextEncoder().encode(verifier));
  const hashString = String.fromCharCode(...Array.from(hashArray));
  const challenge = base64URLEncode(hashString);
  return { codeVerifier: verifier, codeChallenge: challenge };
}

export function toTitleCase(str: string) {
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export function toTitleCaseWithUnderscores(str: string) {
  return str
    .split(/[\s_]+/) // Split by spaces or underscores
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export function slugify(source?: string) {
  if (!source) return "";

  return source
    .toLowerCase()
    .trim() // make lower case and trim
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // remove accents from charaters
    .replace(/[^a-z0-9\s-]/g, " ")
    .trim() // replace invalid chars with spaces
    .replace(/[\s-]+/g, "_"); // replace multiple spaces or hyphens with a single hyphen
}

export function deslugify(slug: string) {
  let deslugged = slug.replace(/-/g, " ");
  deslugged = deslugged.replace(/_/g, " ");
  deslugged = deslugged.replace(/\b\w/g, (char) => char.toUpperCase());
  return deslugged;
}

export function formatRowString(row_string: string) {
  return row_string
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export async function copyToClipboard(value: string, itemName: string) {
  if (typeof window === "undefined") return;
  try {
    await navigator.clipboard.writeText(value);
  } catch {
    toast.error(`Failed to copy ${itemName} to clipboard`);
    return;
  }
  toast.success(`Copied ${itemName} to clipboard`);
}

export function getHrefPathname(href: string) {
  let url: URL;
  const baseURL = new URL(window.location.href).origin;
  if (href.toString().startsWith("http")) {
    url = new URL(href.toString());
  } else {
    url = new URL(href.toString(), baseURL);
  }
  return url.pathname;
}

export const VALIDATION_PATTERNS = {
  NUMBER: /^-?\d+(\.\d+)?$/,
  INT: /^-?\d+$/,
  BOOLEAN: /^(true|false|1|0)$/i,
} as const;

export const VALIDATION_RULES = {
  STRING: (value: string) => typeof value === "string",
  NUMBER: (value: string) => VALIDATION_PATTERNS.NUMBER.test(value.trim()),
  INT: (value: string) => VALIDATION_PATTERNS.INT.test(value.trim()),
  BOOLEAN: (value: string) =>
    VALIDATION_PATTERNS.BOOLEAN.test(String(value).trim()),
} as const;

export function createModelFieldMappingOption(
  modelField: EntityModelFieldResponse[number],
): MappingOption {
  if (modelField.data_type === "COLLECTION") {
    return {
      id: modelField.id,
      label: modelField.label,
      type: "group",
      entity_type: modelField.entity_type,
      path: modelField.label,
      children: modelField.children.map<MappingOptionItem>((child) => ({
        id: child.id,
        label: child.label,
        type: child.data_type,
        entity_type: child.entity_type,
        path: `${modelField.label}: ${child.label}`,
        value: `${modelField.key}_${child.key}`,
      })),
    };
  }
  return {
    id: modelField.id,
    label: modelField.label,
    type: modelField.data_type,
    entity_type: modelField.entity_type,
    path: modelField.label,
    value: modelField.key,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function deepEqual(a: any, b: any): boolean {
  if (a === b) return true;

  if (
    typeof a !== "object" ||
    a === null ||
    typeof b !== "object" ||
    b === null
  ) {
    return false;
  }

  // Arrays
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i])) return false;
    }
    return true;
  }

  // Objects
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);

  if (aKeys.length !== bKeys.length) return false;

  for (const key of aKeys) {
    if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
    if (!deepEqual(a[key], b[key])) return false;
  }

  return true;
}

export function camelToReadable(str: string) {
  return str
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2") // insert space before capital letters
    .replace(/^./, (s) => s.toUpperCase()); // capitalize first letter
}

export function convertByteSizeToReadable(size: number) {
  if (size > 1e9) return `${(size / 1e9).toFixed(2)} GB`;
  if (size > 1e6) return `${(size / 1e6).toFixed(2)} MB`;
  if (size > 1e3) return `${(size / 1e3).toFixed(2)} KB`;
  return `${size} B`;
}

export function toBase64(txt: string) {
  // TextEncoder: Always UTF8
  const uint8Array = new TextEncoder().encode(txt);
  let binary = "";

  for (let i = 0; i < uint8Array.length; ++i)
    binary += String.fromCharCode(uint8Array[i]);

  return btoa(binary);
}
